import { styled } from "@mui/material/styles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button as MuiButton,
  Checkbox,
  Stack,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import React, { useState, useEffect, useMemo } from "react";
import { endOfDay, startOfDay, format } from "date-fns";
import { AcquireBookDataGrid } from "../../../components/DataGrid";
import {
  type GridColDef,
  type GridRenderCellParams,
  type GridColumnHeaderParams,
  type GridSelectionModel,
  type GridRowParams,
  GridToolbarContainer,
  useGridApiContext,
  type GridValueGetterParams,
} from "@mui/x-data-grid-pro";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import DownloadingIcon from "@mui/icons-material/Downloading";
import Tooltip from "@mui/material/Tooltip";
import {
  type AcquireBookErrorType,
  type AcquireBookRealEstateType,
  type AcquireBookStatusFilter,
} from "../types";
import IconButton from "@mui/material/IconButton";
import { useFeatureFlags } from "@/configs/featureFlag";
import Typography from "@mui/material/Typography";
import { GetPrefecturesAPI } from "@/api/GetPrefecturesAPI";
import type { IPrefecture } from "@/types/prefectures";
import { useApiClient } from "@/hooks/useApiClient";
import { useCityWithExternalState } from "@/hooks/useCity";
import { type ISingleBookAcquireDialogData } from "../../../types/singleBookAcquireDialog";
import { ButtonColorOption } from "@/components/Button";
import { SelectLocationModal } from "@/components/AcquireBook/SelectLocationModal";
import {
  type AcquireBookErrorBadge,
  ERROR_DEFINITIONS,
  ERROR_TYPE_LABELS,
  ERROR_TYPE_OPTIONS,
} from "../utils/errorClassifier";
import {
  type AcquireBookStatusCellMeta,
  type AcquireBookStatusDataGridRow,
} from "../utils/mergeRowsByProperty";
import { normalizeTextForSearch } from "../utils/normalizeTextForSearch";
import {
  useExcelExport,
  type ExcelExportOptions,
} from "@/hooks/useExcelExport";
import {
  getPrefectureIdByValue,
  getPrefectureNameById,
  getPrefectureSelectObject,
} from "@/utils/utilsAcquireBook";
import { toast } from "react-toastify";
import { FilterPanel } from "./FilterPanel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SalesforceTransferStateCell } from "@/features/salesforceIntegration/components/SalesforceTransferStateCell";
import { resolveRowTransferState } from "@/features/salesforceIntegration/utils/resolveRowTransferState";
import { type TransferEligibilityRealEstate } from "@/features/salesforceIntegration/types";

interface IProps {
  convertedRows: AcquireBookStatusDataGridRow[];
  selectedOwnerInfos: Map<number, boolean>;
  setSelectedOwnerInfos: React.Dispatch<
    React.SetStateAction<Map<number, boolean>>
  >;
  bindOwnerAnalyzeRequestId: number | null | undefined;
  selectedAllMattersInfos: Map<number, boolean>;
  setSelectedAllMattersInfos: React.Dispatch<
    React.SetStateAction<Map<number, boolean>>
  >;
  bindAllMattersAnalyzeRequestId: number | null | undefined;
  setAcquireBookOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSingleBookAcquireDialogData: React.Dispatch<
    React.SetStateAction<ISingleBookAcquireDialogData>
  >;
  selectedRowIds: GridSelectionModel;
  setSelectedRowIds: React.Dispatch<React.SetStateAction<GridSelectionModel>>;
  identificationName: string | null | undefined;
  /** true のとき会社名列を表示する（DataLens 一括依頼の詳細のみ） */
  isDatalensRequest?: boolean;
  /** true のとき Salesforce 列を表示する（契約と機能フラグが揃った利用者のみ） */
  showSalesforceTransfer?: boolean;
  /** 物件単位の連携可否。Salesforce 列が行の状態を引くのに使う */
  salesforceRealEstateByUniqueId?: ReadonlyMap<
    string,
    TransferEligibilityRealEstate
  >;
  /** 実行中の連携ジョブへ送った物件。送った行だけを「連携中」にする */
  salesforceInFlightUniqueIds?: ReadonlySet<string>;
}

/** 判定結果が未取得の間に使う空の辞書。毎レンダーで生成しないよう固定する */
const EMPTY_REAL_ESTATE_MAP: ReadonlyMap<
  string,
  TransferEligibilityRealEstate
> = new Map();

/** 連携を実行していない間に使う空集合。毎レンダーで生成しないよう固定する */
const EMPTY_IN_FLIGHT_UNIQUE_IDS: ReadonlySet<string> = new Set();

const STATUS_BADGE_COLUMN_WIDTH = 200;

type StatusMetaField =
  | "allMattersInfoMeta"
  | "ownerInfoMeta"
  | "electricDrawingsMeta"
  | "tisekiDrawingsMeta"
  | "tiekiDrawingsMeta"
  | "buildingDrawingsMeta";

type StatusValueField =
  | "allMattersInfo"
  | "ownerInfo"
  | "electricDrawings"
  | "tisekiDrawings"
  | "tiekiDrawings"
  | "buildingDrawings";

const FAILED_BADGE_FALLBACK: AcquireBookErrorBadge =
  ERROR_DEFINITIONS.GENERIC_FAILED;

const REAL_ESTATE_TYPE_OPTIONS: AcquireBookRealEstateType[] = [
  "LAND",
  "BUILDING",
  "CONDOMINIUM",
];

const REAL_ESTATE_TYPE_LABELS: Record<AcquireBookRealEstateType, string> = {
  LAND: "土地",
  BUILDING: "建物",
  CONDOMINIUM: "区分建物",
};

const DEFAULT_FILTER: AcquireBookStatusFilter = {
  errorTypes: [],
  prefecture: null,
  cityKeyword: "",
  locationKeyword: "",
  chibanKeyword: "",
  realEstateTypes: [...REAL_ESTATE_TYPE_OPTIONS],
  acquiredDateFrom: null,
  acquiredDateTo: null,
  freeKeyword: "",
};

const SELECT_ALL_VALUE = "__ALL__";
const FREE_KEYWORD_DEBOUNCE = 500;

const getStartOfDayTime = (value: Date): number => startOfDay(value).getTime();

const getEndOfDayTime = (value: Date): number => endOfDay(value).getTime();

const StyledDataGrid = styled(AcquireBookDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

interface ExportToolbarProps {
  fileName: string;
  sheetName: string;
}

const ExportToolbar: React.FC<ExportToolbarProps> = ({
  fileName,
  sheetName,
}) => {
  const { downloadAsExcelFile, makeExcelUint8Array, getFeedGridData } =
    useExcelExport();
  const apiRef = useGridApiContext();
  const exportOptions: ExcelExportOptions = {
    allColumns: true,
    fileName,
    sheetName,
  };
  const resolvedFileName =
    exportOptions.fileName ??
    format(new Date(), "yyyyMMdd_取得済不動産登記一覧");
  const resolvedSheetName = exportOptions.sheetName ?? "取得済不動産登記";

  const handleExcelExport = (): void => {
    const arrayOfArray = getFeedGridData(apiRef, exportOptions);
    const data = makeExcelUint8Array(arrayOfArray, resolvedSheetName);
    downloadAsExcelFile(data, resolvedFileName);
  };

  const handleCsvExport = (): void => {
    apiRef.current.exportDataAsCsv({
      fileName,
      utf8WithBom: true,
      allColumns: true,
    });
  };

  return (
    <GridToolbarContainer
      sx={{
        padding: "6px 16px",
      }}
    >
      <Stack direction={"row"} spacing={1}>
        <MuiButton variant={"outlined"} onClick={handleExcelExport}>
          エクセル出力
          <DownloadIcon />
        </MuiButton>
        <MuiButton variant={"outlined"} onClick={handleCsvExport}>
          CSV出力
          <DownloadIcon />
        </MuiButton>
      </Stack>
    </GridToolbarContainer>
  );
};

const checkboxHelpOwnerMessage =
  "所有者事項解析を行う場合はチェックしてください";
const checkboxHelpAllMattersMessage =
  "全部事項解析を行う場合はチェックしてください";
const otherUserAnalyzeDisabledMessage =
  "他ユーザーが取得した登記は解析できません";

export const FeedGrid: React.FC<IProps> = (props) => {
  const {
    convertedRows,
    selectedOwnerInfos,
    setSelectedOwnerInfos,
    bindOwnerAnalyzeRequestId,
    selectedAllMattersInfos,
    setSelectedAllMattersInfos,
    bindAllMattersAnalyzeRequestId,
    setAcquireBookOpen,
    setSingleBookAcquireDialogData,
    selectedRowIds,
    setSelectedRowIds,
  } = props;

  // テーブルヘッダーの「所有者事項解析」チェックボックスの状態
  const [selectAllOwnerInfo, setSelectAllOwnerInfo] = useState<boolean>(false);

  // テーブルヘッダーの「全部事項解析」チェックボックスの状態
  const [selectAllAllMattersInfo, setSelectAllAllMattersInfo] =
    useState<boolean>(false);

  const [filter, setFilter] = useState<AcquireBookStatusFilter>(DEFAULT_FILTER);
  const [freeKeywordInput, setFreeKeywordInput] = useState<string>(
    DEFAULT_FILTER.freeKeyword
  );
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [, setInputTextDirectly] = useState<boolean>(true);
  const [selectedCityCode, setSelectedCityCode] = useState<string>("");
  const [prefectureSelectValue, setPrefectureSelectValue] =
    useState<string>("");
  const [realEstateTypeSelectValue, setRealEstateTypeSelectValue] =
    useState<string>("");
  const exportFileName = useMemo(() => {
    const datePrefix = format(new Date(), "yyyyMMdd");
    const base = "取得済不動産登記一覧";
    const trimmedName = props.identificationName?.trim();
    if (trimmedName) {
      return `${datePrefix}_${trimmedName}_${base}`;
    }
    return `${datePrefix}_${base}`;
  }, [props.identificationName]);
  const exportSheetName = "取得済不動産登記";

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilter((prev) =>
        prev.freeKeyword === freeKeywordInput
          ? prev
          : { ...prev, freeKeyword: freeKeywordInput }
      );
    }, FREE_KEYWORD_DEBOUNCE);

    return () => {
      window.clearTimeout(timer);
    };
  }, [freeKeywordInput]);

  const normalizedFilter = useMemo(
    () => ({
      cityKeyword: normalizeTextForSearch(filter.cityKeyword),
      locationKeyword: normalizeTextForSearch(filter.locationKeyword),
      chibanKeyword: normalizeTextForSearch(filter.chibanKeyword),
      freeKeyword: normalizeTextForSearch(filter.freeKeyword),
    }),
    [
      filter.cityKeyword,
      filter.locationKeyword,
      filter.chibanKeyword,
      filter.freeKeyword,
    ]
  );

  const filteredRows = useMemo(() => {
    if (convertedRows.length === 0) return [];
    const fromTime =
      filter.acquiredDateFrom !== null
        ? getStartOfDayTime(filter.acquiredDateFrom)
        : null;
    const toTime =
      filter.acquiredDateTo !== null
        ? getEndOfDayTime(filter.acquiredDateTo)
        : null;
    const errorTypeSet = new Set(filter.errorTypes);
    const realEstateTypeSet = new Set(filter.realEstateTypes);

    return convertedRows.filter((row) => {
      if (!realEstateTypeSet.has(row.realEstateType)) return false;
      if (filter.prefecture && row.prefectureName !== filter.prefecture)
        return false;
      if (
        normalizedFilter.cityKeyword &&
        !row.normalizedLocation.includes(normalizedFilter.cityKeyword)
      ) {
        return false;
      }
      if (
        normalizedFilter.locationKeyword &&
        !row.normalizedLocation.includes(normalizedFilter.locationKeyword)
      ) {
        return false;
      }
      if (
        normalizedFilter.chibanKeyword &&
        !row.normalizedChiban.includes(normalizedFilter.chibanKeyword)
      ) {
        return false;
      }
      if (
        normalizedFilter.freeKeyword &&
        !row.normalizedFreeWord.includes(normalizedFilter.freeKeyword)
      ) {
        return false;
      }
      if (fromTime !== null) {
        if (
          row.pictureReceivedDate === null ||
          row.pictureReceivedDate.getTime() < fromTime
        ) {
          return false;
        }
      }
      if (toTime !== null) {
        if (
          row.pictureReceivedDate === null ||
          row.pictureReceivedDate.getTime() > toTime
        ) {
          return false;
        }
      }
      if (filter.errorTypes.length > 0) {
        if (row.errorTypes.length === 0) return false;
        const hasSelectedError = row.errorTypes.some((type) =>
          errorTypeSet.has(type)
        );
        if (!hasSelectedError) return false;
      }

      return true;
    });
  }, [
    convertedRows,
    filter.errorTypes,
    filter.prefecture,
    filter.realEstateTypes,
    filter.acquiredDateFrom,
    filter.acquiredDateTo,
    normalizedFilter.cityKeyword,
    normalizedFilter.locationKeyword,
    normalizedFilter.chibanKeyword,
    normalizedFilter.freeKeyword,
  ]);

  const [prefectures, setPrefectures] = useState<IPrefecture[]>([]);
  const { apiClient } = useApiClient();
  useEffect(() => {
    let isMounted = true;
    (async () => {
      // 都道府県リストをAPIから取得する。
      const prefectures = await GetPrefecturesAPI(apiClient);
      if (isMounted) {
        setPrefectures(prefectures);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!filter.prefecture) {
      if (prefectureSelectValue !== "") {
        setPrefectureSelectValue("");
      }
      return;
    }
    const id = getPrefectureIdByValue(filter.prefecture);
    const nextValue = id != null ? String(id) : "";
    if (nextValue !== prefectureSelectValue) {
      setPrefectureSelectValue(nextValue);
    }
  }, [filter.prefecture, prefectureSelectValue]);

  useEffect(() => {
    // Select 表示値を filter.realEstateTypes と同期
    if (
      filter.realEstateTypes.length === REAL_ESTATE_TYPE_OPTIONS.length &&
      realEstateTypeSelectValue !== ""
    ) {
      setRealEstateTypeSelectValue("");
      return;
    }
    if (
      filter.realEstateTypes.length === 1 &&
      filter.realEstateTypes[0] !== realEstateTypeSelectValue
    ) {
      setRealEstateTypeSelectValue(filter.realEstateTypes[0]);
    }
  }, [filter.realEstateTypes, realEstateTypeSelectValue]);

  const prefectureOptions = useMemo(() => {
    return getPrefectureSelectObject();
  }, []);

  const selectedPrefectureId = prefectureSelectValue
    ? Number(prefectureSelectValue)
    : 0;
  const { cityList } = useCityWithExternalState(
    [prefectureSelectValue].filter((value) => value !== ""),
    filter.cityKeyword,
    (initialCityCode) => {
      if (initialCityCode && initialCityCode !== selectedCityCode) {
        setSelectedCityCode(initialCityCode);
      }
    }
  );

  const handlePrefectureSelectChange = (
    event: SelectChangeEvent<string>
  ): void => {
    const value = event.target.value;
    setPrefectureSelectValue(value);
    const prefectureName =
      value === "" ? null : getPrefectureNameById(Number(value)) ?? null;
    setFilter((prev) => ({
      ...prev,
      prefecture: prefectureName,
      cityKeyword: "",
      locationKeyword: "",
    }));
    setSelectedCityCode("");
  };

  const getCityNameByCode = (code: string): string => {
    if (!code) return "";
    return cityList?.list.find((city) => city.cityCode === code)?.name ?? "";
  };

  const handleCitySelectChange = (event: SelectChangeEvent<string>): void => {
    const value = event.target.value;
    setSelectedCityCode(value);
    setFilter((prev) => ({
      ...prev,
      cityKeyword: value === "" ? "" : getCityNameByCode(value),
    }));
  };

  const handleRealEstateTypeSelectChange = (
    event: SelectChangeEvent<string>
  ): void => {
    const value = event.target.value;
    setRealEstateTypeSelectValue(value);
    setFilter((prev) => ({
      ...prev,
      realEstateTypes:
        value === ""
          ? [...REAL_ESTATE_TYPE_OPTIONS]
          : [value as AcquireBookRealEstateType],
    }));
  };

  const handleLocationKeywordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setFilter((prev) => ({
      ...prev,
      locationKeyword: event.target.value,
    }));
  };

  const handleChibanKeywordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setFilter((prev) => ({
      ...prev,
      chibanKeyword: event.target.value,
    }));
  };

  const handleFreeKeywordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setFreeKeywordInput(event.target.value);
  };

  const handleErrorTypesChange = (
    event: SelectChangeEvent<AcquireBookStatusFilter["errorTypes"]>
  ): void => {
    const value = event.target.value;
    const parsedValue = Array.isArray(value) ? value : value.split(",");

    const isErrorType = (item: string): item is AcquireBookErrorType =>
      ERROR_TYPE_OPTIONS.includes(item as AcquireBookErrorType);

    const nextValue = parsedValue.filter((item) => {
      return isErrorType(item) || item === SELECT_ALL_VALUE;
    });

    if (nextValue.includes(SELECT_ALL_VALUE)) {
      const shouldSelectAll = filter.errorTypes.length === 0;
      setFilter((prev) => ({
        ...prev,
        errorTypes: shouldSelectAll ? [...ERROR_TYPE_OPTIONS] : [],
      }));
      return;
    }

    const selectedErrorTypes = nextValue.filter(isErrorType);

    setFilter((prev) => ({
      ...prev,
      errorTypes: selectedErrorTypes,
    }));
  };

  const handleResetFilters = (): void => {
    setFilter({
      ...DEFAULT_FILTER,
      realEstateTypes: [...REAL_ESTATE_TYPE_OPTIONS],
    });
    setFreeKeywordInput(DEFAULT_FILTER.freeKeyword);
    setSelectedCityCode("");
    setPrefectureSelectValue("");
    setRealEstateTypeSelectValue("");
  };

  const handleLocationSelectFromModal = (value: string): void => {
    setFilter((prev) => ({
      ...prev,
      locationKeyword: value,
    }));
  };

  const handleLocationModalSetCity = (cityCode: string): void => {
    setSelectedCityCode(cityCode);
    const cityName = getCityNameByCode(cityCode);
    setFilter((prev) => ({
      ...prev,
      cityKeyword: cityName,
    }));
  };

  const { ownerInfoViaScraping } = useFeatureFlags();

  const existValidInfo = (
    type: "owner" | "allMatters",
    row: AcquireBookStatusDataGridRow
  ): boolean => {
    if (row.isOtherUserResource) return false;
    const info = type === "owner" ? row.ownerInfo : row.allMattersInfo;
    return info !== "" && info !== "FAILED" && info !== "PROCESSING";
  };

  const handleRowSelectionChange = (
    newSelectionModel: GridSelectionModel
  ): void => {
    setSelectedRowIds(newSelectionModel);
  };

  /**
   * チェックの活性/非活性を制御する関数
   * @description
   * - 全部事項、所有者事項、電子公図、地積測量図、地役権図面、建物図面のパラメータが設定されていて、かつその中で"FAILED"でない値が設定されている場合はtrueを返す
   * @param params
   * @returns boolean
   */
  const isRowSelectable = (params: GridRowParams): boolean => {
    const row = params.row as AcquireBookStatusDataGridRow;
    const targets = [
      row.allMattersInfo,
      row.ownerInfo,
      row.electricDrawings,
      row.tisekiDrawings,
      row.tiekiDrawings,
      row.buildingDrawings,
    ];
    return targets.some((target) => {
      return (
        target !== undefined &&
        target !== "" &&
        target !== "FAILED" &&
        target !== "PROCESSING"
      );
    });
  };

  /**
   * テーブルヘッダーの所有者事項解析全選択チェックボックスを操作した時の関数
   * @param event
   */
  const handleSelectAllOwnerInfoChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const newChecked = new Map<number, boolean>(selectedOwnerInfos);
    convertedRows.forEach((row) => {
      if (!existValidInfo("owner", row)) return;

      newChecked.set(row.ownerInfoDetailId, event.target.checked);
    });
    setSelectedOwnerInfos(newChecked);
    setSelectAllOwnerInfo(event.target.checked);
  };

  /**
   * テーブルヘッダーの全部事項解析全選択チェックボックスを操作した時の関数
   * @param event
   */
  const handleSelectAllAllMattersInfoChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const newChecked = new Map<number, boolean>(selectedAllMattersInfos);
    convertedRows.forEach((row) => {
      if (!existValidInfo("allMatters", row)) return;

      newChecked.set(row.allMattersDetailId, event.target.checked);
    });
    setSelectedAllMattersInfos(newChecked);
    setSelectAllAllMattersInfo(event.target.checked);
  };

  /**
   * セルの値（引数として渡されたvalueの値）に応じて適切なアイコンを表示する関数
   * @param value 次のいずれかの値が引数として渡されると想定している [undefined, "", "FAILED", "PROCESSING", <URL>]
   * @description valueの値と出力の関係は下記の通り
   * - undefined : 空欄
   * - "" : 空欄
   * - "FAILED" : エラーアイコン
   * - "PROCESSING" : ダウンロード中アイコン
   * - <URL> : ダウンロードアイコン
   */
  const StatusBadge = ({
    badge,
  }: {
    badge: AcquireBookErrorBadge;
  }): JSX.Element => {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-block",
          px: 1,
          py: 0.25,
          fontSize: "0.75rem",
          borderRadius: "999px",
          border: `2px solid ${badge.color.text}`,
          backgroundColor: "transparent",
          color: badge.color.text,
          fontWeight: 600,
          minWidth: 72,
          textAlign: "center",
        }}
      >
        {badge.label}
      </Box>
    );
  };

  const getErrorTypeLabel = (
    meta: AcquireBookStatusCellMeta | null
  ): string | null => {
    if (meta?.badge?.type != null) {
      return ERROR_TYPE_LABELS[meta.badge.type];
    }
    if (meta?.badge?.label) return meta.badge.label;
    return null;
  };

  const formatFailedStatus = (
    meta: AcquireBookStatusCellMeta | null
  ): string => {
    const typeLabel = getErrorTypeLabel(meta);
    const detail = meta?.detail?.trim();
    if (typeLabel && detail) return `${typeLabel}: ${detail}`;
    if (typeLabel) return typeLabel;
    if (detail) return detail;
    return ERROR_TYPE_LABELS.GENERIC_FAILED;
  };

  const formatStatusForExport = (
    value: string,
    meta: AcquireBookStatusCellMeta | null
  ): string => {
    if (!value) return "";
    if (value === "FAILED") {
      return formatFailedStatus(meta);
    }
    if (value === "PROCESSING") return "取得中";
    return "取得済み";
  };

  const formatDateTime = (value: Date | null): string => {
    if (!value) return "";
    return format(value, "yyyy/MM/dd HH:mm");
  };

  const renderStatusCell = (
    valueField: StatusValueField,
    metaField: StatusMetaField
  ) =>
    function StatusCell({
      row,
    }: GridRenderCellParams<string, AcquireBookStatusDataGridRow>):
      | React.ReactNode
      | string {
      const cellValue = row[valueField];
      if (cellValue === undefined || cellValue === "") return "";
      const meta = row[metaField];

      if (cellValue === "FAILED") {
        const badge = meta?.badge ?? FAILED_BADGE_FALLBACK;
        return (
          <Tooltip title={meta?.detail ?? "取得に失敗しました"}>
            <span>
              <StatusBadge badge={badge} />
            </span>
          </Tooltip>
        );
      }

      if (cellValue === "PROCESSING") {
        return (
          <Tooltip title={"取得しています"}>
            <DownloadingIcon />
          </Tooltip>
        );
      }

      return (
        <Tooltip title={"ダウンロードできます"}>
          <IconButton href={cellValue}>
            <CloudDownloadOutlinedIcon />
          </IconButton>
        </Tooltip>
      );
    };

  /**
   * @param value AcquireBookStatusDataGridRow
   * @return ISingleBookAcquireDialogData
   * @description AcquireBookStatusDataGridRow型のデータをISingleBookAcquireDialogData型に変換する
   */
  const transferSingleBookAcquireDialogData = (
    value: AcquireBookStatusDataGridRow
  ): ISingleBookAcquireDialogData => {
    return {
      prefecturesId:
        prefectures.find(
          (prefecture) => prefecture.name === value.prefectureName
        )?.id ??
        getPrefectureIdByValue(value.prefectureName) ??
        0,
      prefectures: value.prefectureName,
      address: value.location,
      chiban: value.realEstateType === "LAND" ? value.chibanKaokuNumber : "",
      kaokuNumber:
        value.realEstateType !== "LAND" ? value.chibanKaokuNumber : "",
      realEstateTypeEng: value.realEstateType,
    };
  };

  // カラム設定
  const makeColDef = (): GridColDef[] => {
    const colDef: GridColDef[] = [
      {
        field: "reacquisition",
        headerName: "再取得",
        width: 120,
        sortable: false,
        headerAlign: "center",
        align: "center",
        disableExport: true,
        renderCell: (
          params: GridRenderCellParams<string, AcquireBookStatusDataGridRow>
        ): React.ReactNode => {
          const { row } = params;

          return row.status === "SUCCEEDED" ? (
            <MuiButton
              variant="text"
              color={ButtonColorOption.Primary}
              onClick={() => {
                setAcquireBookOpen(true);
                setSingleBookAcquireDialogData(
                  transferSingleBookAcquireDialogData(row)
                );
              }}
            >
              再取得に進む
            </MuiButton>
          ) : (
            <Typography sx={{ fontSize: "0.75em" }}>ー</Typography>
          );
        },
      },
      // DataLens 一括依頼のときのみ会社名列を出す（列順は再取得の後）。
      // 通常依頼は既存の列構成のまま
      ...(props.isDatalensRequest === true
        ? [
            {
              field: "companyName",
              headerName: "会社名",
              width: 260,
              sortable: true,
            },
          ]
        : []),
      // 横スクロールせずに状態が見えるよう、物件を特定する列より前に置く
      ...(props.showSalesforceTransfer === true
        ? [
            {
              field: "salesforceTransferState",
              headerName: "Salesforce",
              width: 160,
              sortable: false,
              headerAlign: "center" as const,
              align: "center" as const,
              disableExport: true,
              renderCell: ({
                row,
              }: GridRenderCellParams<
                string,
                AcquireBookStatusDataGridRow
              >): React.ReactNode => (
                <SalesforceTransferStateCell
                  state={resolveRowTransferState(
                    row.uniqueIds,
                    props.salesforceRealEstateByUniqueId ??
                      EMPTY_REAL_ESTATE_MAP,
                    props.salesforceInFlightUniqueIds ??
                      EMPTY_IN_FLIGHT_UNIQUE_IDS
                  )}
                />
              ),
            },
          ]
        : []),
      {
        field: "realEstateType",
        headerName: "種別",
        width: 120,
        sortable: true,
        renderCell: ({ value }: GridRenderCellParams<string>) => value,
        valueGetter: (
          params: GridValueGetterParams<
            AcquireBookRealEstateType,
            AcquireBookStatusDataGridRow
          >
        ): string => {
          const type = params.row.realEstateType;
          return REAL_ESTATE_TYPE_LABELS[type] ?? "";
        },
      },
      {
        field: "prefectureName",
        headerName: "都道府県",
        width: 110,
        sortable: true,
      },
      {
        field: "location",
        headerName: "所在",
        width: 260,
        sortable: true,
      },
      {
        field: "chibanKaokuNumber",
        headerName: "地番または家屋番号",
        width: 190,
        sortable: true,
      },
      {
        field: "allMattersInfo",
        headerName: "全部事項",
        width: STATUS_BADGE_COLUMN_WIDTH,
        sortable: true,
        headerAlign: "center",
        align: "center",
        renderCell: renderStatusCell("allMattersInfo", "allMattersInfoMeta"),
        valueGetter: (
          params: GridValueGetterParams<string, AcquireBookStatusDataGridRow>
        ): string =>
          formatStatusForExport(
            params.row.allMattersInfo,
            params.row.allMattersInfoMeta
          ),
      },
      {
        field: "allMattersInfoDate",
        headerName: "全部事項取得日",
        width: 180,
        sortable: true,
        hide: true,
        type: "dateTime",
        valueGetter: (
          params: GridValueGetterParams<string, AcquireBookStatusDataGridRow>
        ): string => formatDateTime(params.row.allMattersInfoDate),
      },
      {
        field: "allMattersInfoCheckBox",
        headerName: "全部事項チェックボックス",
        width: 170,
        sortable: false,
        headerAlign: "center",
        align: "center",
        disableExport: true,
        renderCell: (
          params: GridRenderCellParams<string, AcquireBookStatusDataGridRow>
        ): React.ReactNode => {
          const { row } = params;

          return (
            <Tooltip
              title={
                row.isOtherUserResource
                  ? otherUserAnalyzeDisabledMessage
                  : checkboxHelpAllMattersMessage
              }
              placement={"right-start"}
            >
              <span>
                <Checkbox
                  checked={
                    selectedAllMattersInfos.get(row.allMattersDetailId) ?? false
                  }
                  onChange={(e) => {
                    const newChecked = new Map<number, boolean>(
                      selectedAllMattersInfos
                    );
                    newChecked.set(row.allMattersDetailId, e.target.checked);
                    if (selectAllAllMattersInfo) {
                      setSelectAllAllMattersInfo(false);
                    }
                    setSelectedAllMattersInfos(newChecked);
                  }}
                  disabled={!existValidInfo("allMatters", row)}
                />
              </span>
            </Tooltip>
          );
        },
        renderHeader: (_params: GridColumnHeaderParams) => (
          <>
            <Typography
              fontSize={"0.875rem"}
              fontWeight={700}
              sx={{ color: "rgba(0,0,0,0.87)" }}
            >
              全部事項解析
            </Typography>
            <Tooltip title={checkboxHelpAllMattersMessage} placement={"top"}>
              <Checkbox
                checked={selectAllAllMattersInfo}
                onChange={handleSelectAllAllMattersInfoChange}
                disabled={convertedRows.every(
                  (row) => !existValidInfo("allMatters", row)
                )}
              />
            </Tooltip>
          </>
        ),
      },
      {
        field: "ownerInfo",
        headerName: "所有者事項",
        width: STATUS_BADGE_COLUMN_WIDTH,
        sortable: true,
        headerAlign: "center",
        align: "center",
        renderCell: renderStatusCell("ownerInfo", "ownerInfoMeta"),
        valueGetter: (
          params: GridValueGetterParams<string, AcquireBookStatusDataGridRow>
        ): string =>
          formatStatusForExport(params.row.ownerInfo, params.row.ownerInfoMeta),
      },
      {
        field: "ownerInfoDate",
        headerName: "所有者事項取得日",
        width: 180,
        sortable: true,
        hide: true,
        type: "dateTime",
        valueGetter: (
          params: GridValueGetterParams<string, AcquireBookStatusDataGridRow>
        ): string => formatDateTime(params.row.ownerInfoDate),
      },
      {
        field: "ownerInfoCheckBox",
        headerName: "所有者事項チェックボックス",
        width: 170,
        sortable: false,
        headerAlign: "center",
        align: "center",
        disableExport: true,
        renderCell: (
          params: GridRenderCellParams<string, AcquireBookStatusDataGridRow>
        ): React.ReactNode => {
          const { row } = params;

          return (
            <Tooltip
              title={
                row.isOtherUserResource
                  ? otherUserAnalyzeDisabledMessage
                  : checkboxHelpOwnerMessage
              }
              placement={"right-start"}
            >
              <span>
                <Checkbox
                  checked={
                    selectedOwnerInfos.get(row.ownerInfoDetailId) ?? false
                  }
                  onChange={(e) => {
                    const newChecked = new Map<number, boolean>(
                      selectedOwnerInfos
                    );
                    newChecked.set(row.ownerInfoDetailId, e.target.checked);
                    if (selectAllOwnerInfo) {
                      setSelectAllOwnerInfo(false);
                    }
                    setSelectedOwnerInfos(newChecked);
                  }}
                  disabled={!existValidInfo("owner", row)}
                />
              </span>
            </Tooltip>
          );
        },
        renderHeader: (_params: GridColumnHeaderParams) => (
          <>
            <Typography
              fontSize={"0.875rem"}
              fontWeight={700}
              sx={{ color: "rgba(0,0,0,0.87)" }}
            >
              所有者事項解析
            </Typography>
            <Tooltip title={checkboxHelpOwnerMessage} placement={"top"}>
              <Checkbox
                checked={selectAllOwnerInfo}
                onChange={handleSelectAllOwnerInfoChange}
                disabled={convertedRows.every(
                  (row) => !existValidInfo("owner", row)
                )}
              />
            </Tooltip>
          </>
        ),
      },
      {
        field: "electricDrawings",
        headerName: "電子公図",
        width: STATUS_BADGE_COLUMN_WIDTH,
        sortable: true,
        headerAlign: "center",
        align: "center",
        renderCell: renderStatusCell(
          "electricDrawings",
          "electricDrawingsMeta"
        ),
        valueGetter: (
          params: GridValueGetterParams<string, AcquireBookStatusDataGridRow>
        ): string =>
          formatStatusForExport(
            params.row.electricDrawings,
            params.row.electricDrawingsMeta
          ),
      },
      {
        field: "electricDrawingsDate",
        headerName: "電子公図取得日",
        width: 180,
        sortable: true,
        hide: true,
        type: "dateTime",
        valueGetter: (
          params: GridValueGetterParams<string, AcquireBookStatusDataGridRow>
        ): string => formatDateTime(params.row.electricDrawingsDate),
      },
      {
        field: "tisekiDrawings",
        headerName: "地積測量図",
        width: STATUS_BADGE_COLUMN_WIDTH,
        sortable: true,
        headerAlign: "center",
        align: "center",
        renderCell: renderStatusCell("tisekiDrawings", "tisekiDrawingsMeta"),
        valueGetter: (
          params: GridValueGetterParams<string, AcquireBookStatusDataGridRow>
        ): string =>
          formatStatusForExport(
            params.row.tisekiDrawings,
            params.row.tisekiDrawingsMeta
          ),
      },
      {
        field: "tisekiDrawingsDate",
        headerName: "地積測量図取得日",
        width: 180,
        sortable: true,
        hide: true,
        type: "dateTime",
        valueGetter: (
          params: GridValueGetterParams<string, AcquireBookStatusDataGridRow>
        ): string => formatDateTime(params.row.tisekiDrawingsDate),
      },
      {
        field: "tiekiDrawings",
        headerName: "地役権図面",
        width: STATUS_BADGE_COLUMN_WIDTH,
        sortable: true,
        headerAlign: "center",
        align: "center",
        renderCell: renderStatusCell("tiekiDrawings", "tiekiDrawingsMeta"),
        valueGetter: (
          params: GridValueGetterParams<string, AcquireBookStatusDataGridRow>
        ): string =>
          formatStatusForExport(
            params.row.tiekiDrawings,
            params.row.tiekiDrawingsMeta
          ),
      },
      {
        field: "tiekiDrawingsDate",
        headerName: "地役権図面取得日",
        width: 180,
        sortable: true,
        hide: true,
        type: "dateTime",
        valueGetter: (
          params: GridValueGetterParams<string, AcquireBookStatusDataGridRow>
        ): string => formatDateTime(params.row.tiekiDrawingsDate),
      },
      {
        field: "buildingDrawings",
        headerName: "建物図面",
        width: STATUS_BADGE_COLUMN_WIDTH,
        sortable: true,
        headerAlign: "center",
        align: "center",
        renderCell: renderStatusCell(
          "buildingDrawings",
          "buildingDrawingsMeta"
        ),
        valueGetter: (
          params: GridValueGetterParams<string, AcquireBookStatusDataGridRow>
        ): string =>
          formatStatusForExport(
            params.row.buildingDrawings,
            params.row.buildingDrawingsMeta
          ),
      },
      {
        field: "buildingDrawingsDate",
        headerName: "建物図面取得日",
        width: 180,
        sortable: true,
        hide: true,
        type: "dateTime",
        valueGetter: (
          params: GridValueGetterParams<string, AcquireBookStatusDataGridRow>
        ): string => formatDateTime(params.row.buildingDrawingsDate),
      },
    ];

    // 全部事項解析済みならチェックボックスのカラムを非表示にする
    if (bindAllMattersAnalyzeRequestId) {
      const index = colDef.findIndex(
        (column) => column.field === "allMattersInfoCheckBox"
      );
      if (index !== -1) {
        colDef.splice(index, 1);
      }
    }

    // ownerInfoViaScraping無効の場合はチェックボックスのカラムを非表示にする
    // 所有者事項解析済みならチェックボックスのカラムを非表示にする
    if (!ownerInfoViaScraping || bindOwnerAnalyzeRequestId) {
      const index = colDef.findIndex(
        (column) => column.field === "ownerInfoCheckBox"
      );
      if (index !== -1) {
        colDef.splice(index, 1);
      }
    }

    return colDef;
  };

  return (
    <>
      <Accordion defaultExpanded={false} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={700}>検索条件を開く</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FilterPanel
            filter={filter}
            realEstateTypeSelectValue={realEstateTypeSelectValue}
            prefectureSelectValue={prefectureSelectValue}
            selectedPrefectureId={selectedPrefectureId}
            selectedCityCode={selectedCityCode}
            prefectureOptions={prefectureOptions}
            cityList={cityList ?? { list: [] }}
            freeKeywordInput={freeKeywordInput}
            errorTypeOptions={ERROR_TYPE_OPTIONS}
            errorTypeLabels={ERROR_TYPE_LABELS}
            realEstateTypeOptions={REAL_ESTATE_TYPE_OPTIONS}
            realEstateTypeLabels={REAL_ESTATE_TYPE_LABELS}
            selectAllValue={SELECT_ALL_VALUE}
            onChangeErrorTypes={handleErrorTypesChange}
            onChangeRealEstateTypeSelect={handleRealEstateTypeSelectChange}
            onChangePrefecture={handlePrefectureSelectChange}
            onChangeCity={handleCitySelectChange}
            onChangeLocationKeyword={handleLocationKeywordChange}
            onChangeChibanKeyword={handleChibanKeywordChange}
            onChangeFreeKeyword={handleFreeKeywordChange}
            onChangeAcquiredDateStart={(date) => {
              setFilter((prev) => ({ ...prev, acquiredDateFrom: date }));
            }}
            onChangeAcquiredDateEnd={(date) => {
              setFilter((prev) => ({ ...prev, acquiredDateTo: date }));
            }}
            onClickSelectLocation={() => {
              if (!filter.prefecture) {
                toast.info("都道府県を選択してください");
                return;
              }
              setIsLocationModalOpen(true);
            }}
            disableSelectLocationButton={!filter.prefecture}
            onResetFilters={handleResetFilters}
          />
        </AccordionDetails>
      </Accordion>

      <StyledDataGrid
        stickyHeader={true}
        autoHeight
        checkboxSelection
        onSelectionModelChange={handleRowSelectionChange}
        selectionModel={selectedRowIds}
        isRowSelectable={isRowSelectable}
        disableColumnMenu
        disableSelectionOnClick={true}
        rows={filteredRows}
        columns={makeColDef()}
        components={{
          Toolbar: ExportToolbar,
        }}
        componentsProps={{
          toolbar: {
            fileName: exportFileName,
            sheetName: exportSheetName,
          },
        }}
      />

      <SelectLocationModal
        open={isLocationModalOpen}
        onClose={() => {
          setIsLocationModalOpen(false);
        }}
        prefectureId={selectedPrefectureId}
        cityInfo={{
          setCode: handleLocationModalSetCity,
          listResponse: cityList ?? { list: [] },
        }}
        setLocationWithoutCity={handleLocationSelectFromModal}
        setInputTextDirectly={setInputTextDirectly}
      />
    </>
  );
};
